import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  Flex,
  FormControl,
  Button,
  Input,
  InputRightElement,
  InputGroup,
  IconButton,
  Container,
  Spinner, useToast
} from "@chakra-ui/react";
import { useHistory, Link } from "react-router-dom";
import MultiSelect from "react-multi-select-component";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";

import Layout from "@pages/Layout";
import { Logo, IconEye, IconTriangle, IconSuccess, IconLocation } from "@assets/Icons";
import { registerCompany } from "@lib/datahandlers/auth";
import { dataList } from "@lib/datahandlers/global";
import { ModalBox, View, Separator, ErrorBox } from "@components";
import { registerCompanySchema } from "@lib/validationData";
import { showPasswordHandler } from "@lib/functions";
import { t } from "@lang/i18n";

// Import React Script Libraray to load Google object
import Script from 'react-load-script';
import { getPredictions } from '@lib/autocomplete';
import { google_api_key } from "@config";


import { chakra, ChakraProvider, extendTheme, } from "@chakra-ui/react";
import { matchSorter } from "match-sorter";

import {
  theme as selectTheme,
  SelectSingle,
  SelectControl,
  SelectMenu,
  SelectMenuList,
  SelectOption,
  SelectValueContainer,
  SelectButton,
  ArrowIndicator,
  SelectSearchInput
} from "chakra-ui-select";

const theme = extendTheme({
  components: { ...selectTheme }
});


// const fruitValues = fruits.map((item) => item.value);
const itemToString = (item) => item?.label ?? "";

const multiselect_strings = {
  allItemsAreSelected: t("multiselect.allItemsAreSelected"),
  clearSearch: t("multiselect.clearSearch"),
  noOptions: t("multiselect.noOptions"),
  search: t("multiselect.search"),
  selectAll: t("multiselect.selectAll"),
  selectSomeItems: t("multiselect.selectSomeItems"),
};

const RegisterCompanyScreen = () => {
  const initForm = {
    longitude: "",
    latitude: "",
    department: "",
    region: "",
    city: ""
  };
  const initDataList = {
    activities: [],
    fonctions: [],
  };

  const [form, setForm] = useState(initForm);
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectData, setSelectData] = useState(initDataList);
  const [showPassword, setShowPassword] = useState(false);
  const [showModalBox, setShowModalBox] = useState(false);

  const history = useHistory();
  const toast = useToast();
  const { register, handleSubmit, setValue, getValues, setError, control, formState: { errors } } = useForm({
    resolver: yupResolver(registerCompanySchema)
  });

  // filter (select)
  const getFilteredItems = (items, inputValue) => {
    return matchSorter(items, inputValue ?? "", { keys: ["label"] });
  };

  const handleShowClick = () => {
    setShowPassword(!showPassword);
  };
  
  const preventDefaultSubmit = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };

  const errorHandling = (obj) => {
    return Object.keys(obj).map(function(key) {
      if (obj[key].length === 1)  setError(key, {
                                    type: "manual",
                                    message: obj[key],
                                  });
      else setError(key, {
            type: "manual",
            message: obj[key],
          });
    });
  };

  const onRegister = async (data) => {
    if (!data.isChecked || data.isChecked === 'undefined') return;

    const { fonctions, activities, name, password, email, siret, address } = data;
    const { region, department, city, longitude, latitude } = form;

    let new_data = {
      fonctions: fonctions.map(el => el.value).join(','),
      activities: activities.value, name, email, siret, address, password,
      region, department, city, longitude, latitude
    };
    
    setIsLoaded(true)
    const result = await registerCompany(new_data);

    if (result.success) {
      setShowModalBox(true);
      setIsLoaded(false);
      return;
    }
    setIsLoaded(false);
    if (errorHandling(result.errorMessages)) errorHandling(result.errorMessages)
    // toast({ description: result.errorMessages ? errorHandling(result.errorMessages) : t('messages.error.register'), status: "error", position: "bottom-right", variant: "left-accent", isClosable: true });
  };

  // load data list
  const getDataList = async () => {
    const data = await dataList();

    setSelectData({
      ...selectData,
      fonctions: data.fonctions.map((el) => ({ value: el.id, label: el.name })),
      activities: data.activities,
    });
  };
  useEffect(() => {
    if (selectData.fonctions.length === 0 || selectData.activities.length === 0) {
      getDataList();
    }
  }, []);

  const onPlaceSelected = (params) => {
    setForm(prevState => {
      return { ...prevState, ...params }
    });
  };

  // set default values to multi select
  useEffect(() => {
    setValue('fonctions', []);
  }, []);

  return (
      <Layout isFooterVisible menuType="company">
        <View if={showModalBox}>
          <ModalBox isOpen={showModalBox}>
            <Box mb="30px">
              <IconSuccess iconType="company" />
            </Box>
            <Text color="black" textAlign="center" fontSize="16px" fontWeight="400">
              {t("company.register.success")}
            </Text>

            <Button bg="green.dark" borderRadius="24px" color="white"
              w="164px" py="20px" mt="26px" _hover={{ bg: "green.dark", opacity: ".7" }}
              onClick={() => {
                setShowModalBox(false)
                history.push('/login')
              }}
            >
              {t("global.close")}
            </Button>
          </ModalBox>
        </View>

        <Container maxW="429px" bg="white" py="39px" px={["16px","","","42px"]} m="63px auto 129px auto" borderRadius="4px" backdropBlur="blur(10px)" opacity=".75">
          <Flex flexDir="column" justifyContent="space-evenly" alignItems="center" textAlign="center">
            <Box mx="auto" mb="16px">
              <Logo logoType="auth" />
            </Box>

            <Box>
              <Text fontWeight="400" fontSize="16px">
                {t("company.register.title.t_1")}
                <Box as="span" fontWeight="600">
                  {t("company.register.title.t_2")}
                </Box>
                {t("company.register.title.t_3")}
              </Text>
              <Box as="span" color="green.dark" fontSize="37px" fontWeight="800">
                {t("company.register.offer_a_job")}
              </Box>
            </Box>

            <Separator />

            <form onSubmit={handleSubmit(onRegister)} onKeyDown={(e) => preventDefaultSubmit(e)}>
              <Flex flexDir="column">
                <FormControl id="name" mb="26px">
                  <Input
                    type="text"
                    placeholder={t("fields.company.name")}
                    isInvalid={errors.name ? true : false}
                    focusBorderColor={errors.name ? "error" : "green.light"}
                    errorBorderColor="error"
                    borderColor="gray.borders"
                    borderRadius="4px"
                    bg="white"
                    maxW="21.5rem"
                    h="46px"
                    {...register("name")}
                  />
                  {errors.name && <ErrorBox error={errors.name.message} width="21.5rem" />}
                </FormControl>

                <Script url={`https://maps.googleapis.com/maps/api/js?key=${google_api_key}&libraries=places`}
                  onLoad={() => getPredictions({ inputId:'autocomplete', callback: onPlaceSelected })}
                />
                <FormControl id="address" mb="26px" pos="relative">
                  <InputGroup>
                    <Input
                      type="text"
                      placeholder={t("fields.company.siege")}
                      isInvalid={errors.address ? true : false}
                      errorBorderColor="error"
                      borderColor="gray.borders"
                      borderRadius="4px"
                      bg="white" h="46px"
                      focusBorderColor={errors.address ? "error" : "green.light"}
                      autoComplete='off'
                      id="autocomplete"
                      {...register("address")}
                    />

                    <InputRightElement
                      children={<IconLocation iconType="company" />}
                      top=".1rem"
                    />
                  </InputGroup>
                  {errors.address && <ErrorBox error={errors.address.message} width="21.5rem" />}
                </FormControl>

                <FormControl id="siret" mb="26px">
                  <Input
                    type="text"
                    placeholder={t("fields.company.siret")}
                    isInvalid={errors.siret ? true : false}
                    errorBorderColor="error"
                    borderColor="gray.borders"
                    borderRadius="4px"
                    bg="white"
                    maxW="21.5rem"
                    h="46px"
                    focusBorderColor={errors.siret ? "error" : "green.light"}
                    {...register("siret")}                    
                  />
                  {errors.siret && <ErrorBox error={errors.siret.message} width="21.5rem" />}
                </FormControl>

                <FormControl id="activities" mb="26px">
                  <Controller name="activities" render={({ field }) => (
                      <ChakraProvider theme={theme}>
                        <SelectSingle maxW="21.5rem" borderColor="gray" itemToString={itemToString}
                              {...field}>
                          { ({ inputValue }) => {
                              const items = getFilteredItems(
                                selectData.activities && selectData.activities.map((el) => ({ value: el.id, label: el.name })),
                                inputValue
                              );
                            return (
                              <>
                                <SelectControl
                                  border={errors.activities ? "2px solid" : "1px solid"} 
                                  borderColor={errors.activities ? "#EB5757" : "#888"}
                                  borderRadius="4px" bg="white" maxW="21.5rem" h="46px" 
                                  >
                                  <SelectValueContainer>
                                    <SelectSearchInput placeholder={t("fields.company.activities")} />
                                  </SelectValueContainer>
                                  <SelectButton aria-label="toggle menu">
                                    <ArrowIndicator>
                                      <IconTriangle iconType="company" position="down" />
                                    </ArrowIndicator>
                                  </SelectButton>
                                </SelectControl>

                                <SelectMenu>
                                  <SelectMenuList zIndex="999">
                                    {items.map((option, index) => (
                                      <SelectOption
                                        key={option.value}
                                        value={option}
                                        index={index}
                                        textAlign="left"
                                        _hover={{ backgroundColor: "#73C316", color: "white", cursor: "pointer" }}
                                      >
                                        {itemToString(option)}
                                      </SelectOption>
                                    ))}
                                    {items.length <= 0 && (
                                      <chakra.div py={2} pl={3} pr={9}>
                                        Aucun
                                      </chakra.div>
                                    )}
                                  </SelectMenuList>
                                </SelectMenu>
                              </>
                            );
                          }}
                        </SelectSingle>
                    </ChakraProvider>
                    )}
                    control={control}
                    defaultValue={""}
                  />
                  {errors.activities && <ErrorBox error={errors.activities.message} width="21.5rem" />}
                </FormControl>

                <FormControl id="email" mb="26px">
                  <Input
                    type="email"
                    placeholder={t("fields.email")}
                    isInvalid={errors.email ? true : false}
                    errorBorderColor="error"
                    borderColor="gray.borders"
                    borderRadius="4px"
                    bg="white"
                    maxW="21.5rem"
                    h="46px"
                    focusBorderColor={errors.email ? "error" : "green.light"}
                    {...register("email")}
                  />
                  {errors.email && <ErrorBox error={errors.email.message} width="21.5rem" />}
                </FormControl>

                <FormControl id="password" mb="26px">
                  <InputGroup justifyContent="center">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder={t("fields.password")}
                      isInvalid={errors.password ? true : false}
                      errorBorderColor="error"
                      maxW="21.5rem"
                      h="46px"
                      borderColor="gray.borders"
                      borderRadius="4px"
                      bg="white"
                      focusBorderColor={errors.password ? "error" : "green.light"}
                      {...register("password")}
                    />

                    <InputRightElement top="8%" right="2%">
                      <IconButton
                        onClick={handleShowClick}
                        icon={<IconEye opened={showPasswordHandler(showPassword)} />}
                        bg="invisible"
                        _hover={{ bg: "invisible" }}
                        _focus={{ outline: "none" }}
                        _active={{ bg: "white" }}
                      />
                    </InputRightElement>
                  </InputGroup>
                  {errors.password && <ErrorBox error={errors.password.message} width="21.5rem" />}
                </FormControl>

                <Text fontSize="16px" fontWeight="700" my="10px">
                  {t("company.register.question_fonctions")}
                </Text>
                <FormControl id="fonctions" mb="1.625rem" maxW="21.5rem">
                  <Controller name="fonctions" render={({ field }) => (
                      <MultiSelect
                          {...field}
                          options={selectData.fonctions}
                          onChange={(val) => setValue('fonctions', val)}
                          ArrowRenderer={() => (<IconTriangle iconType="company" position="down" />)}
                          valueRenderer={(val) => !val.length ? t("fields.company.fonctions") : null}
                          className={("className", errors.fonctions ? "multi-select company register error" : "multi-select company register")}
                          overrideStrings={multiselect_strings}
                      />
                    )}
                    control={control}
                    defaultValue={""}
                  />
                  
                  {errors.fonctions && <ErrorBox error={errors.fonctions.message} width="21.5rem" />}
                </FormControl>
              </Flex>

                <label className={"custom-checkbox custom-checkbox-register"} tab-index="0" aria-label={"isChecked"}>
                    <input type="checkbox" id={"isChecked"} value={true} {...register("isChecked")} />
                    <span className={"checkmark company"}></span>
                    <label className="label" htmlFor={"isChecked"}>
                      <Text fontWeight="400" fontSize="14px" my="1rem" maxW="18rem">
                        { t('company.register.conditions.t_1') }
                        <a href={`https://www.visiojob.fr/legal/privacy_notice?type=company`} className="link-password-forgot company">
                          { t('company.register.conditions.t_2') }
                        </a>
                        { t('company.register.conditions.t_3') }
                      </Text>
                    </label>
                </label>
                {errors.isChecked && <ErrorBox error={errors.isChecked.message} width="21.5rem" />}

                <Button type="submit" w={["75vw",  "18rem", "", ""]} mt="1rem" h="40px" borderRadius="24px" bg="green.dark" color="white"
                  _hover={{ bg: "green.light" }}>
                  {isLoaded? <Spinner size="md" /> : t("company.register.button")}
                </Button>                
            </form>

            <Link to="/login" className="login-link company">
              { t('global.login') }
            </Link>
          </Flex>
          
      </Container>
    </Layout>
  );
};

export default RegisterCompanyScreen;
